package com.hyperether.goodjob.scenes.user

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.text.selection.DisableSelection
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.material.Divider
import androidx.compose.material.Text
import androidx.compose.material3.DrawerState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.navigation.NavHostController
import com.hyperether.goodjob.Dp
import com.hyperether.goodjob.Sp
import com.hyperether.goodjob.getPlatformChecker
import com.hyperether.goodjob.models.Team
import com.hyperether.goodjob.models.User
import com.hyperether.goodjob.models.UserStatus
import com.hyperether.goodjob.navigation.AddNewUser
import com.hyperether.goodjob.navigation.WebNavigationState
import com.hyperether.goodjob.repository.prefs.PrefsManager
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.add_employees
import com.hyperether.goodjob.resources.add_team
import com.hyperether.goodjob.resources.all
import com.hyperether.goodjob.resources.cancel
import com.hyperether.goodjob.resources.create_your_first_team
import com.hyperether.goodjob.resources.edit
import com.hyperether.goodjob.resources.employees
import com.hyperether.goodjob.resources.error
import com.hyperether.goodjob.resources.no_employees_added
import com.hyperether.goodjob.resources.ok
import com.hyperether.goodjob.resources.start_by_adding
import com.hyperether.goodjob.resources.success
import com.hyperether.goodjob.resources.teams
import com.hyperether.goodjob.resources.total
import com.hyperether.goodjob.resources.user
import com.hyperether.goodjob.scenes.components.ErrorDialog
import com.hyperether.goodjob.scenes.components.FilterButton
import com.hyperether.goodjob.scenes.components.MobileHeaderSearchAdd
import com.hyperether.goodjob.scenes.components.PopUpDialog
import com.hyperether.goodjob.scenes.components.SearchField
import com.hyperether.goodjob.scenes.components.SearchTeamSelector
import com.hyperether.goodjob.scenes.register.FilledButton
import com.hyperether.goodjob.scenes.register.FullScreenProgressBar
import com.hyperether.goodjob.scenes.user.components.EditDialog
import com.hyperether.goodjob.scenes.user.components.FilterUsersDialog
import com.hyperether.goodjob.scenes.user.components.UserList
import com.hyperether.goodjob.theme.BlueApp
import com.hyperether.goodjob.theme.TextColor
import com.hyperether.goodjob.util.Constants.DEFAULT_EMPLOYEE_ID
import com.hyperether.goodjob.util.FilterDialogStateManager
import com.hyperether.resources.stringResource

@Composable
fun UsersScreen(
    isMobileView: Boolean,
    userViewModel: UserViewModel,
    navController: NavHostController,
    prefsManager: PrefsManager,
    drawerState: DrawerState,
    screenHeight: Float = 0f
) {

    val isAllSelected = remember { mutableStateOf(true) }
    val hideEmptyTeamsView = remember { mutableStateOf(false) }
    val isInEditMode = remember { mutableStateOf(false) }
    val isInSearchMode = remember { mutableStateOf(false) }
    val searchQuery = remember { mutableStateOf("") }

    val showPopup = remember { mutableStateOf(false) }
    val teams by userViewModel.teams.collectAsState(emptyList())
    var selectedTeams by remember { mutableStateOf<List<Team>>(emptyList()) }
    val userList by userViewModel.users.collectAsStateWithLifecycle()
    val usersFiltered by userViewModel.filteredUsers.collectAsStateWithLifecycle()

    val usersSelected = remember { mutableStateListOf<User>() }
    LaunchedEffect(Unit) {
        if (getPlatformChecker().isMobile()) {
            userViewModel.loadRemoteData()
        }
        FilterDialogStateManager.setDialogShowing(false)
    }

    if (isMobileView) {
        Box(modifier = Modifier.fillMaxSize()) {
            Column {
                MobileHeaderSearchAdd(
                    title = stringResource(Res.string.employees),
                    drawerState = drawerState,
                    isInDeleteMode = selectedTeams.isNotEmpty(),
                    isAllSelected = isAllSelected.value,
                    onDeleteClick = {
                        userViewModel.showLoader.value = true
                        val selectedTeamIds = selectedTeams.mapNotNull { it._id }
                        val selectedTeamList = teams.filter { it._id in selectedTeamIds }
                        userViewModel.deleteSelectedTeams(
                            selectedTeamList,
                            onAllDeleted = {
                                selectedTeams = emptyList()
                            },
                            onError = {
                            }
                        )
                    },
                    scope = rememberCoroutineScope(),
                    add = {
                        if (getPlatformChecker().isMobile()) {
                            navController.navigate(AddNewUser(DEFAULT_EMPLOYEE_ID))
                            WebNavigationState.lastAccessedIds.remove("add_new_job")
                        } else {
                            navController.navigate(AddNewUser(""))
                        }
                    },
                    search = {
                        isInSearchMode.value = true
                    },
                    onCancelSearch = {
                        isInSearchMode.value = false
                        searchQuery.value = ""
                    },
                    user = prefsManager.getUser()
                )

                if (isInSearchMode.value) {
                    Spacer(modifier = Modifier.height(Dp(15f)))
                    SearchField(searchQuery)
                } else {
                    Column(
                        modifier = Modifier
                            .padding(Dp(8f)),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Row(
                            modifier = Modifier.fillMaxWidth().height(Dp(40f)),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.SpaceEvenly
                        ) {
                            Box(
                                modifier = Modifier
                                    .weight(0.5f)
                                    .fillMaxSize()
                                    .clickable(
                                        interactionSource = remember { MutableInteractionSource() },
                                        indication = null
                                    ) {
                                        isAllSelected.value = true
                                    },
                                contentAlignment = Alignment.Center
                            ) {
                                androidx.compose.material3.Text(
                                    text = stringResource(Res.string.all),
                                    color = if (isAllSelected.value) BlueApp else Color.Gray,
                                    fontSize = Sp(16f),
                                    textAlign = TextAlign.Center
                                )
                            }

                            Box(
                                modifier = Modifier
                                    .weight(0.5f)
                                    .fillMaxSize()
                                    .clickable(
                                        interactionSource = remember { MutableInteractionSource() },
                                        indication = null
                                    ) {
                                        isAllSelected.value = false
                                        isInEditMode.value = false
                                        showPopup.value = false
                                        usersSelected.clear()
                                    },
                                contentAlignment = Alignment.Center
                            ) {
                                androidx.compose.material3.Text(
                                    text = stringResource(Res.string.teams),
                                    color = if (isAllSelected.value) Color.Gray else BlueApp,
                                    fontSize = Sp(16f),
                                    textAlign = TextAlign.Center
                                )
                            }
                        }
                    }
                    Box(
                        modifier = Modifier
                            .height(Dp(2f))
                            .fillMaxWidth(0.5f)
                            .background(if (isAllSelected.value) BlueApp else BlueApp)
                            .align(if (isAllSelected.value) Alignment.Start else Alignment.End)
                    )
                    Divider(
                        modifier = Modifier.height(Dp(1f))
                            .background(Color.LightGray.copy(alpha = 0.1f))
                    )
                }
                if (isAllSelected.value) {
                    selectedTeams = emptyList()
                    if (!isInSearchMode.value) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(horizontal = Dp(16f), vertical = Dp(10f))
                        ) {
                            DisableSelection {
                                if (!isInEditMode.value) {
                                    Text(
                                        text = stringResource(Res.string.edit),
                                        fontSize = Sp(14f),
                                        color = TextColor,
                                        modifier = Modifier
                                            .clickable {
                                                isInEditMode.value = true
                                                showPopup.value = true
                                            }
                                            .weight(1f),
                                        textAlign = TextAlign.Start
                                    )
                                } else {
                                    Text(
                                        text = stringResource(Res.string.cancel),
                                        fontSize = Sp(14f),
                                        color = TextColor,
                                        modifier = Modifier
                                            .clickable {
                                                isInEditMode.value = false
                                                usersSelected.clear()
                                                showPopup.value = false
                                            }
                                            .weight(1f),
                                        textAlign = TextAlign.Start
                                    )
                                }
                            }

                            if (!isInEditMode.value) {
                                val filteredUsersSize = usersFiltered.size
                                val leadingText =
                                    if (filteredUsersSize == 1) stringResource(Res.string.user).replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() } else stringResource(
                                        Res.string.employees
                                    )
                                Text(
                                    text = "${stringResource(Res.string.total)}: $filteredUsersSize $leadingText",
                                    fontSize = Sp(14f),
                                    color = Color.LightGray,
                                    maxLines = 1,
                                    fontStyle = FontStyle.Normal,
                                    textAlign = TextAlign.Center
                                )
                            }

                            Row(
                                horizontalArrangement = Arrangement.End,
                                verticalAlignment = Alignment.CenterVertically,
                                modifier = Modifier.weight(1f)
                            ) {
                                FilterButton()
                            }
                        }
                    }
                    Divider(
                        modifier = Modifier.height(Dp(1f))
                            .background(Color.LightGray.copy(alpha = 0.1f))
                    )
                    // }
                    if (usersFiltered.isEmpty()) {
                        androidx.compose.material3.Text(
                            text = stringResource(Res.string.no_employees_added),
                            color = Color.Black,
                            fontSize = Sp(24f),
                            textAlign = TextAlign.Center,
                            fontWeight = FontWeight.Bold,
                            modifier = Modifier.fillMaxWidth()
                                .padding(top = Dp(60f), start = Dp(16f), end = Dp(16f))
                        )
                        androidx.compose.material3.Text(
                            text = stringResource(Res.string.add_employees),
                            color = Color.Gray,
                            textAlign = TextAlign.Center,
                            fontSize = Sp(16f),
                            modifier = Modifier.padding(
                                top = Dp(20f),
                                start = Dp(16f),
                                end = Dp(16f)
                            )
                        )
                    } else {
                        val sortedUsers = userViewModel.getSortedUsers(searchQuery.value)
                        //change with employee id
                        UserList(
                            users = sortedUsers,
                            isInEditMode = isInEditMode.value,
                            selectedUsers = usersSelected,
                            navHostController = navController
                        )
                    }
                } else {
                    Spacer(modifier = Modifier.height(Dp(15f)))
                    if (teams.isEmpty() && !hideEmptyTeamsView.value) {
                        UserTeams(showAddTeam = {
                            hideEmptyTeamsView.value = true
                        })
                    } else {
                        SearchTeamSelector(
                            teamsFromDb = teams,
                            onTeamsSelected = {
                                selectedTeams = it
                            },
                            onAddNewTeam = {
                                userViewModel.createTeam(it)
                            }
                        )
                    }
                    if (hideEmptyTeamsView.value) {
                        SearchTeamSelector(
                            teamsFromDb = teams,
                            onTeamsSelected = {
                                selectedTeams = it
                            },
                            onAddNewTeam = {
                                userViewModel.createTeam(it)
                            }
                        )
                    }
                }
            }
            if (showPopup.value) {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .background(Color.Black.copy(alpha = 0.05f))
                )

                Box(
                    modifier = Modifier
                        .align(Alignment.BottomEnd)
                ) {
                    EditDialog(
                        showDialog = showPopup.value,
                        employeesListSize = usersSelected.size,
                        onDelete = {
                            userViewModel.onDeleteSelectedUsers(
                                usersSelected = usersSelected,
                                onAllDeleted = {
                                    usersSelected.clear()
                                })
                            showPopup.value = false
                            isInEditMode.value = false
                        },
                        onExportList = {},
                        onImportList = {}
                    )
                }
            }
            if (FilterDialogStateManager.dialogShowing.value) {
                FilterUsersDialog(
                    cityList = userList.mapNotNull { it.city?.trim() }.filter { it.isNotBlank() }
                        .distinct(),
                    roleList = userList.mapNotNull {
                        val role =
                            userViewModel.userRoles.entries.find { entry -> entry.key == it.role }?.value
                        role?.let {
                            stringResource(it)
                        }
                    }.distinct(),
                    teamList = userList.mapNotNull { it.teams }.flatMap { teamList ->
                        teamList.mapNotNull { team ->
                            team?.value?.takeIf { it.isNotBlank() }
                        }
                    }.distinct(),
                    statusList = userList.mapNotNull { user ->
                        if (user.confirmStatus.equals(UserStatus.pending.name, ignoreCase = true)) {
                            val confirmStatus = userViewModel.userStatues.entries.find { entry ->
                                entry.key == user.confirmStatus
                            }?.value

                            confirmStatus?.let {
                                stringResource(it)
                            }
                        } else {
                            val status = userViewModel.userStatues.entries.find { entry ->
                                entry.key == user.status
                            }?.value
                            status?.let {
                                stringResource(it)
                            }
                        }
                    }.distinct(),
                    skillList = userList.flatMap { it.skills ?: emptyList() }.distinct(),
                    viewModel = userViewModel,
                    isMobileView = isMobileView,
                    onDismiss = {
                        FilterDialogStateManager.setDialogShowing(false)
                    },
                    onApply = { selectedCities, selectedRoles, selectedTeams, selectedStatuses, selectedSkills ->
                        FilterDialogStateManager.setDialogShowing(false)
                        userViewModel.onApplyFilter(
                            cities = selectedCities,
                            roles = selectedRoles,
                            teams = selectedTeams,
                            statuses = selectedStatuses,
                            skills = selectedSkills
                        )
                    }
                )
            }
        }
    } else {
        SelectionContainer {
            UsersScreenWeb(
                userViewModel = userViewModel,
                navController = navController,
                isMobileView = isMobileView,
                screenHeight = screenHeight
            )
        }
    }

    if (userViewModel.showPopUpSuccess.value) {
        PopUpDialog(
            onDismiss = { userViewModel.showPopUpSuccess.value = false },
            title = stringResource(Res.string.success).uppercase(),
            message = userViewModel.successText.value,
            onPositiveBtnClicked = { userViewModel.showPopUpSuccess.value = false },
            positiveBtnText = stringResource(Res.string.ok)
        )
    }
    if (userViewModel.showPopUpError.value) {
        ErrorDialog(
            onDismiss = { userViewModel.showPopUpError.value = false },
            title = stringResource(Res.string.error).uppercase(),
            message = userViewModel.errorText.value
        )
    }
    if (userViewModel.showLoader.value) {
        FullScreenProgressBar()
    }
}

@Composable
fun UserTeams(showAddTeam: () -> Unit) {

    Column(
        modifier = Modifier.padding(start = Dp(16f), end = Dp(16f), top = Dp(50f)).fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Text(
            text = stringResource(Res.string.create_your_first_team),
            fontSize = Sp(24f),
            color = Color.Black,
            fontWeight = FontWeight.Bold,
            textAlign = TextAlign.Center

        )
        Spacer(modifier = Modifier.height(Dp(15f)))
        Text(
            text = stringResource(Res.string.start_by_adding),
            fontSize = Sp(16f),
            textAlign = TextAlign.Center
        )
        Spacer(modifier = Modifier.height(Dp(15f)))
        FilledButton(
            onClick = {
                showAddTeam()
            },
            text = stringResource(Res.string.add_team),
            isEnabled = true
        )
    }
}